import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchGroups, fetchLocations, removeWidgetFromGroup, deleteWidget as apiDeleteWidget } from '@/api/endpoints'

export interface Widget {
  id: number
  '@id': string
  description: string
  widget_type: string
  widget_order: number
  widget_width: number
  widget_height: number
  group_count: number
  sensors: Sensor[]
  [key: string]: unknown
}

export interface Sensor {
  id: number
  '@id': string
  name: string
  sensor_type: string
  current: number
  current_date: string
  avg: number
  min: number
  max: number
  short_avg: number
  unit: string
  description: string
  [key: string]: unknown
}

export interface Location {
  id: number
  '@id': string
  city: string
  latitude?: number
  longitude?: number
  altitude?: number
  sunrise?: string | null
  sunset?: string | null
  moonrise?: string | null
  moonset?: string | null
  moon_phase?: {
    key: string
    label: string
    icon: string
    illumination: number
    age_days: number
  } | null
  [key: string]: unknown
}

export interface Group {
  id: number
  '@id': string
  name: string
  entry_order: number
  location: Location
  widgets: Widget[]
  [key: string]: unknown
}

export const useDashboardStore = defineStore('dashboard', () => {
  const locations = ref<Location[]>([])
  const activeLocationId = ref<number | null>(null)
  const groups = ref<Group[]>([])
  const activeGroupId = ref<number | null>(null)
  const widgets = ref<Widget[]>([])
  const loading = ref(false)
  const editMode = ref(false)
  const gridDirty = ref(false)

  const locationGroups = computed(() =>
    groups.value.filter((g) => {
      if (!activeLocationId.value) return true
      const locId = g.location?.['@id']?.match(/\/(\d+)$/)?.[1]
      return locId && Number(locId) === activeLocationId.value
    }),
  )

  async function loadLocations() {
    locations.value = await fetchLocations()
    if (locations.value.length && !activeLocationId.value) {
      activeLocationId.value = locations.value[0].id
    }
  }

  const activeGroup = computed(() =>
    groups.value.find((g) => g.id === activeGroupId.value) ?? null,
  )

  const activeLocationCity = computed(() => {
    const loc = locations.value.find((l) => l.id === activeLocationId.value)
    return loc?.city ?? ''
  })

  async function loadGroups() {
    loading.value = true
    try {
      groups.value = (await fetchGroups()).sort(
        (a: Group, b: Group) => (a.entry_order ?? 0) - (b.entry_order ?? 0),
      )
      // Derive locations from group data if not loaded
      if (!locations.value.length) {
        const locMap = new Map<string, Location>()
        for (const g of groups.value) {
          if (g.location?.['@id']) {
            const idMatch = g.location['@id'].match(/\/(\d+)$/)
            if (idMatch && !locMap.has(g.location['@id'])) {
              locMap.set(g.location['@id'], {
                id: Number(idMatch[1]),
                '@id': g.location['@id'],
                city: g.location.city,
              })
            }
          }
        }
        locations.value = [...locMap.values()]
        if (locations.value.length && !activeLocationId.value) {
          activeLocationId.value = locations.value[0].id
        }
      }
      // Auto-select first group for active location (prefer groups with widgets)
      if (!activeGroupId.value) {
        const filtered = locationGroups.value
        const withWidgets = filtered.find((g) => g.widgets?.length > 0)
        const firstGroup = withWidgets ?? filtered[0]
        if (firstGroup) {
          activeGroupId.value = firstGroup.id
          extractWidgets(firstGroup)
        }
      }
    } finally {
      loading.value = false
    }
  }

  function extractWidgets(group: Group) {
    widgets.value = (group.widgets ?? [])
      .slice()
      .sort((a: Widget, b: Widget) => a.widget_order - b.widget_order)
  }

  function switchGroup(groupId: number) {
    activeGroupId.value = groupId
    const group = groups.value.find((g) => g.id === groupId)
    if (group) {
      extractWidgets(group)
    }
  }

  function switchLocation(locationId: number) {
    activeLocationId.value = locationId
    const filtered = locationGroups.value
    if (filtered.length) {
      switchGroup(filtered[0].id)
    } else {
      activeGroupId.value = null
      widgets.value = []
    }
  }

  function toggleEditMode() {
    editMode.value = !editMode.value
    if (!editMode.value) {
      gridDirty.value = false
    }
  }

  function reorderWidgets(newList: Widget[]) {
    widgets.value = newList.map((w, i) => ({ ...w, widget_order: i }))
    gridDirty.value = true
  }

  function resizeWidget(widgetId: number, prop: 'widget_width' | 'widget_height', direction: 1 | -1) {
    const widths = [12, 25, 33, 50, 66, 75, 100]
    const heights = [0, 20, 30, 40, 50]
    const steps = prop === 'widget_width' ? widths : heights
    const w = widgets.value.find((w) => w.id === widgetId)
    if (!w) return
    const current = w[prop] as number
    const idx = steps.indexOf(current)
    const nextIdx = idx + direction
    if (nextIdx >= 0 && nextIdx < steps.length) {
      w[prop] = steps[nextIdx]
      gridDirty.value = true
    }
  }

  async function saveGrid() {
    if (!widgets.value.length) return
    const { saveWidgetGrid } = await import('@/api/endpoints')
    const gridOptions: Record<number, { order: number; width: number; height: number }> = {}
    for (const w of widgets.value) {
      gridOptions[w.id] = {
        order: w.widget_order,
        width: w.widget_width,
        height: w.widget_height,
      }
    }
    await saveWidgetGrid(gridOptions)
    gridDirty.value = false
    editMode.value = false
  }

  async function removeFromGroup(widgetId: number) {
    if (!activeGroupId.value) return
    await removeWidgetFromGroup(activeGroupId.value, widgetId)
    await loadGroups()
    if (activeGroupId.value) {
      switchGroup(activeGroupId.value)
    }
  }

  async function deleteWidgetEntirely(widgetId: number) {
    await apiDeleteWidget(widgetId)
    await loadGroups()
    if (activeGroupId.value) {
      switchGroup(activeGroupId.value)
    }
  }

  return {
    locations, activeLocationId, groups, locationGroups,
    activeGroupId, activeGroup, activeLocationCity, widgets, loading,
    editMode, gridDirty,
    loadLocations, loadGroups, switchGroup, switchLocation,
    toggleEditMode, reorderWidgets, resizeWidget, saveGrid,
    removeFromGroup, deleteWidgetEntirely,
  }
})
